import { supabase } from '@/integrations/supabase/client';

const BASE = () => (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

async function chamar<T>(path: string, init?: RequestInit): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada. Entre de novo no CRM.');
  const res = await fetch(`${BASE()}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error(json?.error || `Falhou (${res.status})`);
  return (json && typeof json === 'object' && 'data' in json ? json.data : json) as T;
}

export interface BoraFazerAcesso {
  pode: boolean;
  papel: 'consultoria' | 'prime' | 'gestao' | null;
  membro: boolean;
  role: string;
  /** Valor de contrato é confidencial: só master/diretor/gerente. */
  podeValores: boolean;
}

export interface BoraFazerMembro {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  ramal: string | null;
  foto_url: string | null;
  papel: 'consultoria' | 'prime';
}

export interface BoraFazerResumo {
  registros: number;
  encontros: number;
  arquivos: number;
  horas_etapa2: number;
  horas_total: number;
  ultimo_registro: string | null;
  ligacoes_total: number;
  ligacoes_segundos: number;
  ligacoes_transcritas: number;
  tarefas_abertas: number;
  tarefas_atrasadas: number;
  /** Números do contrato — vêm null para quem não pode ver valor. */
  contrato: {
    etapa1_total: number;
    etapa1_parcelas: number;
    etapa1_parcela: number;
    etapa1_primeira: string;
    valor_hora: number;
  } | null;
}

export type BoraFazerTipo = 'nota' | 'encontro' | 'arquivo' | 'marco' | 'decisao' | 'ligacao';

export interface BoraFazerItem {
  id: string;
  tipo: BoraFazerTipo;
  titulo: string;
  descricao: string | null;
  data_evento: string;
  etapa: 'etapa1' | 'etapa2' | null;
  horas: number | string | null;
  autor_id: string | null;
  autor_nome: string | null;
  autor_foto: string | null;
  arquivo_nome?: string | null;
  arquivo_path?: string | null;
  arquivo_tipo?: string | null;
  arquivo_tamanho?: number | null;
  gravacao_id?: string | null;
  duracao_segundos?: number | null;
  transcricao?: string | null;
  transcricao_status?: string | null;
  resumo_ia?: string | null;
  resumo_ia_em?: string | null;
  ramal_origem?: string | null;
  destino?: string | null;
  destino_nome?: string | null;
}

export interface NovoEvento {
  tipo: BoraFazerTipo;
  titulo: string;
  descricao?: string | null;
  data_evento?: string | null;
  etapa?: 'etapa1' | 'etapa2' | null;
  horas?: number | null;
  arquivo_nome?: string | null;
  arquivo_path?: string | null;
  arquivo_tipo?: string | null;
  arquivo_tamanho?: number | null;
}

export const getAcessoBoraFazer = () => chamar<BoraFazerAcesso>('/v1/bora-fazer/acesso');
export const getTimeBoraFazer = () => chamar<BoraFazerMembro[]>('/v1/bora-fazer/time');
export const getResumoBoraFazer = () => chamar<BoraFazerResumo>('/v1/bora-fazer/resumo');

export function getTimelineBoraFazer(filtros?: { tipo?: string; q?: string }): Promise<BoraFazerItem[]> {
  const qs = new URLSearchParams();
  if (filtros?.tipo && filtros.tipo !== 'todos') qs.set('tipo', filtros.tipo);
  if (filtros?.q) qs.set('q', filtros.q);
  const s = qs.toString();
  return chamar<BoraFazerItem[]>(`/v1/bora-fazer/timeline${s ? `?${s}` : ''}`);
}

export const criarEventoBoraFazer = (evento: NovoEvento) =>
  chamar<{ id: string }>('/v1/bora-fazer/eventos', { method: 'POST', body: JSON.stringify(evento) });

export const apagarEventoBoraFazer = (id: string) =>
  chamar<{ id: string }>(`/v1/bora-fazer/eventos/${id}`, { method: 'DELETE' });

/** URL assinada (1h) do áudio da ligação — liberada para quem está no projeto. */
export const getUrlLigacaoBoraFazer = (id: string) =>
  chamar<{ url: string }>(`/v1/bora-fazer/ligacoes/${id}/url`);

// ── Plano de ação e pedidos de informação ────────────────────────────────────

export type TarefaTipo = 'acao' | 'pedido_info';
export type TarefaStatus = 'aberta' | 'fazendo' | 'feita' | 'cancelada';

export interface BoraFazerTarefa {
  id: string;
  tipo: TarefaTipo;
  titulo: string;
  descricao: string | null;
  area: string | null;
  prazo: string | null;
  prioridade: 'alta' | 'media' | 'baixa';
  status: TarefaStatus;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  responsavel_foto: string | null;
  criado_por_nome: string | null;
  criado_em: string;
  concluido_em: string | null;
  atrasada: boolean;
}

export function getTarefasBoraFazer(filtros?: { status?: string; tipo?: string }) {
  const qs = new URLSearchParams();
  if (filtros?.status) qs.set('status', filtros.status);
  if (filtros?.tipo) qs.set('tipo', filtros.tipo);
  const s = qs.toString();
  return chamar<BoraFazerTarefa[]>(`/v1/bora-fazer/tarefas${s ? `?${s}` : ''}`);
}

export const criarTarefaBoraFazer = (t: Partial<BoraFazerTarefa>) =>
  chamar<{ id: string }>('/v1/bora-fazer/tarefas', { method: 'POST', body: JSON.stringify(t) });

export const editarTarefaBoraFazer = (id: string, t: Partial<BoraFazerTarefa>) =>
  chamar<{ id: string }>(`/v1/bora-fazer/tarefas/${id}`, { method: 'PATCH', body: JSON.stringify(t) });

export const apagarTarefaBoraFazer = (id: string) =>
  chamar<{ id: string }>(`/v1/bora-fazer/tarefas/${id}`, { method: 'DELETE' });

// ── Parcelas do contrato ─────────────────────────────────────────────────────

export interface BoraFazerParcela {
  id: string;
  etapa: string;
  numero: number;
  descricao: string | null;
  vencimento: string;
  valor: number;
  pago_em: string | null;
  observacao: string | null;
  atrasada: boolean;
}

export const getParcelasBoraFazer = () => chamar<BoraFazerParcela[]>('/v1/bora-fazer/parcelas');

export const baixarParcelaBoraFazer = (id: string, dados: { pago_em: string | null; observacao?: string | null }) =>
  chamar<{ id: string }>(`/v1/bora-fazer/parcelas/${id}`, { method: 'PATCH', body: JSON.stringify(dados) });

// ── Indicadores ──────────────────────────────────────────────────────────────

export interface BoraFazerIndicador {
  id: string;
  codigo: string;
  rotulo: string;
  meta: number | null;
  ordem: number;
  unidade: 'R$' | 'qtd';
  valor: number;
}

export const getIndicadoresBoraFazer = () => chamar<BoraFazerIndicador[]>('/v1/bora-fazer/indicadores');
export const getCatalogoIndicadores = () =>
  chamar<{ codigo: string; rotulo: string; unidade: 'R$' | 'qtd' }[]>('/v1/bora-fazer/indicadores/catalogo');
export const salvarIndicadorBoraFazer = (codigo: string, meta: number | null) =>
  chamar<{ codigo: string }>('/v1/bora-fazer/indicadores', { method: 'POST', body: JSON.stringify({ codigo, meta }) });
export const removerIndicadorBoraFazer = (id: string) =>
  chamar<{ id: string }>(`/v1/bora-fazer/indicadores/${id}`, { method: 'DELETE' });

// ── Documentos vivos ─────────────────────────────────────────────────────────

export interface BoraFazerDocumento {
  id: string;
  titulo: string;
  area: string | null;
  descricao: string | null;
  versao: number;
  atual: boolean;
  substitui_id: string | null;
  arquivo_nome: string;
  arquivo_path: string;
  arquivo_tipo: string | null;
  arquivo_tamanho: number | null;
  autor_nome: string | null;
  criado_em: string;
}

export const getDocumentosBoraFazer = (historico = false) =>
  chamar<BoraFazerDocumento[]>(`/v1/bora-fazer/documentos${historico ? '?historico=true' : ''}`);

export const salvarDocumentoBoraFazer = (doc: Partial<BoraFazerDocumento> & { arquivo_path: string }) =>
  chamar<{ id: string; versao: number }>('/v1/bora-fazer/documentos', { method: 'POST', body: JSON.stringify(doc) });

export const apagarDocumentoBoraFazer = (id: string) =>
  chamar<{ id: string }>(`/v1/bora-fazer/documentos/${id}`, { method: 'DELETE' });

// ── Relatório mensal da Etapa 2 ──────────────────────────────────────────────

export interface RelatorioHoras {
  competencia: string;
  valor_hora: number;
  total_horas: number;
  total_valor: number;
  itens: { id: string; data_evento: string; titulo: string; descricao: string | null; tipo: string; horas: number; autor_nome: string | null }[];
}

export const getRelatorioHorasBoraFazer = (competencia: string) =>
  chamar<RelatorioHoras>(`/v1/bora-fazer/relatorio-horas?competencia=${encodeURIComponent(competencia)}`);

// ── IA e transcrição ─────────────────────────────────────────────────────────

export const resumirLigacaoBoraFazer = (id: string) =>
  chamar<{ resumo: string }>(`/v1/bora-fazer/ligacoes/${id}/resumo`, { method: 'POST' });

export const lerResumoLigacaoBoraFazer = (id: string) =>
  chamar<{ resumo: string; criado_em: string } | null>(`/v1/bora-fazer/ligacoes/${id}/resumo`);

export const resumirEventoBoraFazer = (id: string) =>
  chamar<{ resumo: string }>(`/v1/bora-fazer/eventos/${id}/resumo`, { method: 'POST' });

/** Manda o áudio anexado (reunião online) pro Whisper local. */
export const transcreverEventoBoraFazer = (id: string) =>
  chamar<{ chars: number }>(`/v1/bora-fazer/eventos/${id}/transcrever`, { method: 'POST' });
